import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" }, 
      { name: "ERP Solution", link: "/erp-solutions" }, 
      { name: "HRM Solution", link: "#" },
      { name: "LMS Solution", link: "#" }, 
      { name: "POS Solution", link: "#" }, 
      { name: "SaaS Platform", link: "#" },
    ],
  },
  {
    label: "Industries",
    dropdown: [
      { name: "Healthcare", link: "#" }, 
      { name: "FinTech", link: "#" }, 
      { name: "EdTech", link: "#" },
      { name: "Retail & E-Commerce", link: "#" }, 
      { name: "Real Estate", link: "#" }, 
      { name: "Logistics", link: "#" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🏗️",
    title: "Custom Web Application",
    desc: "Tailor-made web apps built from scratch — architected for performance, security, and infinite scale. No templates, no compromises.",
    tags: ["React", "Next.js", "Node.js", "PostgreSQL"],
    color: "#3B82F6",
  },
  {
    icon: "🛒",
    title: "E-Commerce Development",
    desc: "High-converting online stores with intelligent product recommendations, seamless checkout flows, and real-time inventory management.",
    tags: ["Shopify", "WooCommerce", "Stripe", "AWS"],
    color: "#8B5CF6",
  },
  {
    icon: "☁️",
    title: "SaaS Platform Development",
    desc: "Multi-tenant SaaS platforms with subscription billing, role-based access, analytics dashboards, and white-label capabilities.",
    tags: ["Next.js", "Prisma", "Stripe", "Redis"],
    color: "#10B981",
  },
  {
    icon: "🏢",
    title: "Enterprise Web Portals",
    desc: "Secure, scalable enterprise portals — ERP, CRM, HRM — with single sign-on, audit trails, and deep third-party integrations.",
    tags: ["Angular", "Java Spring", "Oracle", "Azure"],
    color: "#F59E0B",
  },
  {
    icon: "📊",
    title: "Progressive Web Apps (PWA)",
    desc: "App-like web experiences that work offline, load instantly, and can be installed on any device — no App Store required.",
    tags: ["React", "Service Workers", "IndexedDB", "Push API"],
    color: "#EF4444",
  },
  {
    icon: "🔗",
    title: "API & Backend Development",
    desc: "Robust RESTful and GraphQL APIs, microservices architectures, and headless backends that power your entire digital ecosystem.",
    tags: ["Node.js", "GraphQL", "Docker", "Kubernetes"],
    color: "#EC4899",
  },
  {
    icon: "🎨",
    title: "CMS & Website Development",
    desc: "Beautiful, blazing-fast marketing websites and blogs with headless CMS — easy to edit, impossible to slow down.",
    tags: ["Next.js", "Contentful", "Sanity", "Tailwind"],
    color: "#06B6D4",
  },
  {
    icon: "🔒",
    title: "Web Security & Optimization",
    desc: "Penetration testing, performance audits, Core Web Vitals optimization, and end-to-end encryption to keep your app bulletproof.",
    tags: ["OWASP", "Lighthouse", "Cloudflare", "SSL/TLS"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  { category: "Frontend", items: [
    { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
    { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000" },
    { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
    { name: "Angular", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg", color: "#DD0031" },
    { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
    { name: "Tailwind", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
  ]},
  { category: "Backend", items: [
    { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
    { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
    { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", color: "#FF2D20" },
    { name: "Express", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/express/express-original.svg", color: "#000" },
    { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
    { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg", color: "#777BB4" },
  ]},
  { category: "Database", items: [
    { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
    { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
    { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg", color: "#4479A1" },
    { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
    { name: "Firebase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-plain.svg", color: "#FFCA28" },
    { name: "Supabase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/supabase/supabase-original.svg", color: "#3ECF8E" },
  ]},
  { category: "Cloud & DevOps", items: [
    { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
    { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
    { name: "GitHub", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#181717" },
    { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
    { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
    { name: "Jenkins", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jenkins/jenkins-original.svg", color: "#D33833" },
  ]},
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Discovery & Planning", desc: "In-depth analysis of your business goals, target audience, and technical requirements to craft a precise project roadmap.", color: "#3B82F6" },
  { n: "02", icon: "🎨", title: "UI/UX Design", desc: "User research, wireframes, and high-fidelity prototypes validated with real users before a single line of code is written.", color: "#8B5CF6" },
  { n: "03", icon: "⚙️", title: "Development Sprints", desc: "Agile 2-week sprints with daily standups, live staging environments, and transparent progress tracking via your dashboard.", color: "#10B981" },
  { n: "04", icon: "🧪", title: "QA & Testing", desc: "Automated unit tests, integration tests, cross-browser/device testing, and performance audits before every release.", color: "#F59E0B" },
  { n: "05", icon: "🚀", title: "Launch & Handover", desc: "Zero-downtime deployment, CI/CD pipeline setup, team training, and full documentation handover.", color: "#EF4444" },
  { n: "06", icon: "🛡️", title: "Support & Growth", desc: "Proactive monitoring, monthly performance reports, SLA-backed support, and continuous feature iteration.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "HealthTrack Pro",
    industry: "Healthcare",
    desc: "A HIPAA-compliant telemedicine platform serving 200+ hospitals with real-time monitoring, AI diagnostics, and seamless EHR integration.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Node.js", "AI", "AWS"],
    metrics: [{ val: "200+", lbl: "Hospitals" }, { val: "40%", lbl: "Faster Diagnosis" }, { val: "99.9%", lbl: "Uptime" }],
    color: "#3B82F6",
  },
  {
    title: "FinEdge Portal",
    industry: "FinTech",
    desc: "A real-time financial management SaaS with multi-currency support, AI-driven fraud detection, and seamless bank integrations for 50k+ users.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Python", "PostgreSQL", "Stripe"],
    metrics: [{ val: "50k+", lbl: "Daily Users" }, { val: "₹2Cr+", lbl: "Daily Transactions" }, { val: "4.8★", lbl: "App Rating" }],
    color: "#8B5CF6",
  },
  {
    title: "GetMyProperty",
    industry: "Real Estate",
    desc: "A pan-India real estate marketplace with 3D virtual tours, AI property valuation, and instant mortgage calculators covering 10+ cities.",
    img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Laravel", "Three.js", "MySQL"],
    metrics: [{ val: "10k+", lbl: "Listings" }, { val: "3D", lbl: "Virtual Tours" }, { val: "10+", lbl: "Cities" }],
    color: "#10B981",
  },
];

const FAQS = [
  { q: "How long does a typical web development project take?", a: "It depends on complexity. A marketing website takes 3–6 weeks. A custom web app takes 2–4 months. An enterprise platform can take 4–9 months. We provide a detailed timeline with milestones during the discovery phase." },
  { q: "What is your development pricing model?", a: "We offer both Fixed Price (for well-defined projects) and Time & Material (for evolving requirements). All projects start with a free discovery call and written estimate — no surprise bills, ever." },
  { q: "Do you provide post-launch support?", a: "Absolutely. We offer SLA-backed support packages: Basic (business hours, 48hr SLA), Standard (extended hours, 24hr SLA), and Premium (24/7, 4hr SLA). All packages include proactive monitoring and monthly health reports." },
  { q: "Will my website be mobile-responsive?", a: "100%. Every project we build follows a mobile-first approach. We test across 15+ real device/browser combinations, ensuring pixel-perfect rendering from a 320px phone to a 4K display." },
  { q: "Do you sign NDAs and protect our IP?", a: "Yes. We sign NDAs before any discussion of your project. All IP, source code, and assets created for your project are fully owned by you upon final payment. We never reuse client code." },
  { q: "Can you work with our existing tech team?", a: "Absolutely. We're comfortable in staff-augmentation, co-development, or full-ownership models. We integrate with your Git workflow, Jira, Slack, and coding standards from day one." },
];

const STATS = [
  { val: "560+", lbl: "Web Projects Delivered", icon: "🌐" },
  { val: "8 Yrs", lbl: "Web Dev Expertise", icon: "🏆" },
  { val: "98%", lbl: "On-Time Delivery", icon: "⚡" },
  { val: "4.9★", lbl: "Client Satisfaction", icon: "⭐" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null); // ← NEW

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open ← NEW
  useEffect(() => {
    if (mobileOpen) { document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());
            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => {
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none";
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151";
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          {/* Hamburger — toggles to ✕ when open ← CHANGED */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#EFF6FF" : "none",
            border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>
            {mobileOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ── REPLACED with full accordion version */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #E5E7EB",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
        }}>
          {/* Logo row */}
          <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #F3F4F6" }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>

          {/* Nav links */}
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    {/* Accordion toggle */}
                    <button
                      onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "13px 24px",
                        background: mobileDropdown === link.label ? "#EFF6FF" : "none",
                        border: "none", borderBottom: "1px solid #F3F4F6",
                        color: mobileDropdown === link.label ? "#3B82F6" : "#374151",
                        fontWeight: 600, fontSize: 15,
                        fontFamily: "'Sora',sans-serif", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}
                    >
                      {link.label}
                      <span style={{
                        fontSize: 11, color: "#9CA3AF",
                        transition: "transform .2s",
                        transform: mobileDropdown === link.label ? "rotate(180deg)" : "rotate(0)",
                      }}>▾</span>
                    </button>
                    {/* Dropdown child links */}
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#F8FAFC", borderBottom: "1px solid #F3F4F6" }}>
                        {link.dropdown.map(item => (
                          <a
                            key={item.name}
                            href={item.link}
                            onClick={() => setMobileOpen(false)}
                            style={{
                              display: "block",
                              padding: "11px 24px 11px 40px",
                              fontSize: 14,
                              color: currentPath === item.link ? "#3B82F6" : "#4B5563",
                              background: currentPath === item.link ? "#EFF6FF" : "none",
                              fontWeight: currentPath === item.link ? 700 : 500,
                              textDecoration: "none",
                              borderBottom: "1px solid #F1F5F9",
                              fontFamily: "'Sora',sans-serif",
                            }}
                          >→ {item.name}</a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href={link.link || "#"}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "13px 24px",
                      color: currentPath === link.link ? "#3B82F6" : "#374151",
                      background: currentPath === link.link ? "#EFF6FF" : "none",
                      fontWeight: 600, fontSize: 15,
                      textDecoration: "none", borderBottom: "1px solid #F3F4F6",
                      fontFamily: "'Sora',sans-serif",
                    }}
                  >
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a
              href="#contact"
              onClick={() => setMobileOpen(false)}
              style={{
                background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
                padding: "14px", borderRadius: 10, textAlign: "center",
                fontWeight: 700, fontSize: 15, textDecoration: "none",
                fontFamily: "'Sora',sans-serif",
                boxShadow: "0 6px 20px rgba(59,130,246,.35)",
              }}
            >🚀 Get Free Consultation</a>
            <a
              href="tel:+919646206032"
              style={{
                background: "#F1F5F9", color: "#1E3A8A",
                padding: "14px", borderRadius: 10, textAlign: "center",
                fontWeight: 700, fontSize: 15, textDecoration: "none",
                fontFamily: "'Sora',sans-serif", border: "1px solid #BFDBFE",
              }}
            >📞 Call Us: +91 96462 06032</a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(160deg,#EFF6FF 0%,#F0FDF4 50%,#FFF7ED 100%)",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -100, right: -100, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(59,130,246,.1),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(59,130,246,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,.04) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#6B7280" }}>
          <Link to="/" style={{ color: "#3B82F6", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span>
          <span style={{ color: "#6B7280" }}>Services</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600 }}>Web Development</span>
        </div>

        <div className="wd-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3B82F6", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#1E40AF" }}>🌐 #1 Web Development Company in India</span>
            </div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.5vw,58px)", fontWeight: 900, color: "#0F172A", lineHeight: 1.1, marginBottom: 20 }}>
              Web Development<br />
              <span style={{ background: "linear-gradient(135deg,#1E3A8A,#3B82F6,#06B6D4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                That Drives Growth
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "#6B7280", lineHeight: 1.8, maxWidth: 560, marginBottom: 36 }}>
              From lightning-fast marketing sites to complex enterprise platforms — we build web solutions that are beautiful, performant, secure, and engineered to scale with your ambitions.
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 20px rgba(59,130,246,.4)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(59,130,246,.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(59,130,246,.4)"; }}
              >🚀 Start Your Project</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#1E3A8A", padding: "15px 30px", borderRadius: 10,
                fontWeight: 700, fontSize: 15, textDecoration: "none", border: "2px solid #BFDBFE",
                transition: "all .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#93C5FD"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
              >📂 View Case Studies</a>
            </div>
            {/* Trust signals */}
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[["560+", "Web Projects"], ["8 Yrs", "Experience"], ["98%", "On-Time"], ["4.9★", "Rating"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#1E3A8A" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual */}
          <div style={{ position: "relative" }}>
            <div style={{
              borderRadius: 24, overflow: "hidden",
              boxShadow: "0 40px 100px rgba(59,130,246,.18)",
              border: "1px solid #E5E7EB",
              background: "#fff",
            }}>
              {/* Browser chrome */}
              <div style={{ background: "#F8FAFC", borderBottom: "1px solid #E5E7EB", padding: "12px 20px", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444", "#F59E0B", "#10B981"].map(c => <div key={c} style={{ width: 12, height: 12, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "#EFF6FF", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "#6B7280", fontWeight: 500, marginLeft: 8 }}>
                  🔒 techvanto.com/your-project
                </div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1593720219276-0b1eacd0aef4?w=640&h=400&fit=crop&auto=format"
                alt="Web Development"
                style={{ width: "100%", height: 340, objectFit: "cover", display: "block" }}
              />
            </div>
            {/* Floating badges */}
            <div style={{ position: "absolute", top: -16, left: -20, background: "#fff", borderRadius: 16, padding: "14px 18px", boxShadow: "0 12px 40px rgba(16,185,129,.2)", border: "1.5px solid #D1FAE5", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#065F46" }}>99/100</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500 }}>Lighthouse Score ⚡</div>
            </div>
            <div style={{ position: "absolute", bottom: 20, right: -20, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", borderRadius: 16, padding: "14px 20px", boxShadow: "0 12px 36px rgba(59,130,246,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#fff" }}>2.4x</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500 }}>Avg Conversion Lift</div>
            </div>
            <div style={{ position: "absolute", bottom: -16, left: 20, background: "#fff", borderRadius: 14, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.1)", border: "1.5px solid #FEF3C7", animation: "float 4s ease-in-out infinite 1s", zIndex: 10, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>🚀</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#0F172A", fontFamily: "'Sora',sans-serif" }}>Site Deployed!</div>
                <div style={{ fontSize: 10, color: "#9CA3AF" }}>Build time: 1.2s</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0F172A", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="wd-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "96px 40px", background: "#fff" }} className="wd-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Web Development Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            Full-spectrum web development — from a single landing page to a global enterprise platform.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease", cursor: "pointer",
                transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const points = [
    { icon: "⚡", title: "Performance First", desc: "Every project targets a 90+ Lighthouse score. We obsess over Core Web Vitals, lazy loading, and edge caching so your users never wait.", color: "#3B82F6" },
    { icon: "🔒", title: "Security by Design", desc: "OWASP Top 10 mitigations, end-to-end encryption, regular pen-testing, and SOC 2 compliant infrastructure baked in from day one.", color: "#10B981" },
    { icon: "📱", title: "Mobile-First Always", desc: "Designed and tested on 15+ real device/browser combinations. Pixel-perfect from a 320px iPhone SE to a 4K ultrawide monitor.", color: "#8B5CF6" },
    { icon: "🔄", title: "Agile & Transparent", desc: "Bi-weekly demos, a live staging URL from day 1, and real-time access to your project dashboard — you're always in control.", color: "#F59E0B" },
    { icon: "🧠", title: "Full-Stack Expertise", desc: "25+ engineers across frontend, backend, DevOps, and QA. One team that owns every layer of your product — no handoff headaches.", color: "#EF4444" },
    { icon: "🚀", title: "Post-Launch Growth", desc: "We don't disappear after launch. Monthly analytics reviews, A/B testing, and feature iterations keep your product growing.", color: "#EC4899" },
  ];
  const [hov, setHov] = useState(null);

  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="wd-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Built Different, Delivered Better</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>What separates a good agency from a great one — and why 1,500+ clients keep coming back.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {points.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0F172A", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="wd-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Cutting-Edge Technologies</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>We pick the best tool for each job — not the trendiest. Here's what powers our web solutions.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#1E3A8A,#3B82F6)" : "#fff",
              borderColor: active === i ? "#3B82F6" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(59,130,246,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#fff", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, boxShadow: hov === i ? `0 4px 16px ${tech.color}25` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:24px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "96px 40px", background: "linear-gradient(160deg,#EFF6FF 0%,#F0FDF4 100%)" }} className="wd-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#10B981", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>How We Build Your Product</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>A transparent, milestone-driven process that keeps you informed and in control at every stage.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step, i) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 20, padding: "32px 28px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 4px 20px rgba(0,0,0,.05)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}12`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}20` }}>
                {step.icon}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}15`, padding: "3px 10px", borderRadius: 99 }}>STEP {step.n}</span>
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "96px 40px", background: "#fff" }} className="wd-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Web Apps We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Real projects, real results — a glimpse into the transformative web solutions we've shipped.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 52, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="wd-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 360, objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 28, marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 26, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 26px", borderRadius: 10, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}35`,
                  transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#1E3A8A", border: "2px solid #BFDBFE",
            padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .2s",
            display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#93C5FD"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
          >View All Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="wd-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Everything you need to know before starting your web project.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#3B82F6" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(59,130,246,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 28px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#3B82F6" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, transition: "transform .25s", display: "block", transform: open === i ? "rotate(45deg)" : "none" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 28px 24px", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact CTA ───────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", budget: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#3B82F6" : "#E5E7EB"}`,
    background: focused === n ? "#F0F6FF" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(59,130,246,.1)" : "none",
  });

  return (
    <section id="#contact" style={{ padding: "96px 40px", background: "linear-gradient(160deg,#F8FAFC,#EFF6FF)" }} className="wd-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Let's Build Your Web Product</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Tell us about your project and get a free consultation + detailed quote within 24 hours.</p>
        </div>
        <div className="wd-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#1E3A8A,#2563EB,#3B82F6)", borderRadius: 24, padding: "40px 36px", marginBottom: 24, boxShadow: "0 20px 60px rgba(59,130,246,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Quick Response Guaranteed</h3>
              <p style={{ color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>We respond to every inquiry within 24 hours with a tailored proposal — no generic templates.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "28px", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["🎯", "Project scope & timeline estimate"],
                ["💰", "Transparent fixed-price quote"],
                ["🔒", "NDA signed before any discussion"],
                ["🤝", "Dedicated project manager assigned"],
                ["📊", "Technology recommendation report"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 24, padding: "44px", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(16,185,129,.35)" }}>✓</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Request Received! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our web dev team will review your requirements and get back to you within 24 hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", budget: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Tell Us About Your Project</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>Fill out the form below and we'll create a custom proposal for you.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="wd-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="wd-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Budget Range</label>
                      <select name="budget" value={form.budget} onChange={handleChange} onFocus={() => setFocused("budget")} onBlur={() => setFocused(null)} style={{ ...iStyle("budget"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select budget…</option>
                        {["Under ₹1 Lakh", "₹1–3 Lakhs", "₹3–10 Lakhs", "₹10–25 Lakhs", "₹25 Lakhs+"].map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Project Description *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="Describe what you want to build — the problem it solves, who will use it, any specific features you need…" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your details are safe. We never share your information with third parties.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#93C5FD" : "linear-gradient(135deg,#1E3A8A,#3B82F6)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(59,130,246,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "🚀 Get Free Consultation"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Python & AI"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#3B82F6"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#93C5FD"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function WebDevelopment() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        @keyframes marquee { from { transform:translateX(0); } to { transform:translateX(-50%); } }

        /* ── Desktop base ── */
        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .wd-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .wd-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .wd-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .wd-contact-grid { display:grid; grid-template-columns:1fr 1.5fr; gap:48px; align-items:start; }
        .footer-grid     { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .wd-form-row     { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        /* ── ≤1100px ── */
        @media (max-width:1100px) {
          .wd-hero-grid  { grid-template-columns:1fr 1fr; gap:40px; }
          .footer-grid   { grid-template-columns:1fr 1fr; gap:32px; }
          .wd-stats-grid { grid-template-columns:repeat(2,1fr); }
        }

        /* ── ≤900px ── */
        @media (max-width:900px) {
          .wd-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .wd-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .wd-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links       { display:none !important; }
          .nav-cta         { display:none !important; }
          .mob-menu-btn    { display:flex !important; }
          .wd-section      { padding-left:20px !important; padding-right:20px !important; }
        }

        /* ── ≤640px ── */
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .wd-stats-grid   { grid-template-columns:repeat(2,1fr); gap:24px; }
          .wd-form-row     { grid-template-columns:1fr !important; }
          .wd-section      { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; }
          section          { padding-left:16px !important; padding-right:16px !important; }
          footer           { padding-left:16px !important; padding-right:16px !important; }
        }

        /* ── ≤480px ── */
        @media (max-width:480px) {
          .footer-grid     { grid-template-columns:1fr !important; gap:28px; }
          .wd-stats-grid   { grid-template-columns:1fr 1fr; gap:20px; }
        }

        /* ── ≤500px ── */
        @media (max-width:500px) {
          .topbar-right    { display:none !important; }
        }

        /* ── Navbar padding on mobile ── */
        @media (max-width:640px) {
          nav > div:nth-child(2) { padding: 0 16px !important; }
          nav > div:first-child  { padding: 6px 16px !important; }
        }

        /* ── Hero floating badges — prevent overflow on mobile ── */
        @media (max-width:640px) {
          .wd-hero-grid > div:last-child { margin-top: 8px; }
        }
      `}</style>

      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}